import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import {
  AdminApi,
  AdminApiConnectionCreation,
  AdminApiConnectionsCreateRequest,
  AdminApiConnectionsListTypeEnum,
  AdminUserRetrieve,
  OrganizationPermissionsEnum,
} from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import { DistributionPlatformDropdownOption } from '..';
import Hint from 'src/ui/atoms/hint';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import { currentUserHasManageUsersAndGroupsPermission } from 'src/apps/issuer/helpers/userPermissions';
import { debounce } from 'lodash';

interface AddApiConnectionModalProps {
  onSaveApiConnection: () => void;
  onClose: () => void;
  user: AdminUserRetrieve;
  organizationId: string;
  hasIssuerApiConnection: boolean;
  hasDistributorApiConnection: boolean;
  setCreatedApiConnection: (val: AdminApiConnectionCreation) => void;
  canAddIssuerApiConnection: boolean;
  canAddDistributorApiConnection: boolean;
}

interface AddApiConnectionFields {
  name: string;
  type: AdminApiConnectionsListTypeEnum;
  distributionPlatform: string;
}

export const Form = makeModalForm<AddApiConnectionFields>();

const AddApiConnectionModal: FunctionComponent<AddApiConnectionModalProps> = ({
  onSaveApiConnection,
  onClose,
  user,
  organizationId,
  hasIssuerApiConnection,
  hasDistributorApiConnection,
  setCreatedApiConnection,
  canAddIssuerApiConnection,
  canAddDistributorApiConnection,
}) => {
  const { makeAuthenticatedApi, withApi, error } = useApiCall();
  const [apiIssuerConnectionAlreadyExists, setIssuerApiConnectionAlreadyExists] = useState(false);
  const [distributorConnectionAlreadyExists, setDistributorConnectionAlreadyExists] = useState(false);
  const [distributionPlatforms, setDistributionPlatforms] = useState<DistributionPlatformDropdownOption[]>([]);
  const [selectedDistributionPlatform, setSelectedDistributionPlatform] = useState<string | undefined>();

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const { currentUser } = useCurrentUserSelector();

  useEffect(() => {
    withApi(async () => {
      const res = await adminApi.adminApiConnectionsList({
        organization: organizationId,
        type: [AdminApiConnectionsListTypeEnum.ISSUER_API],
        limit: 1,
      });
      if (
        !!res.results.length &&
        res.results.some((connection) => connection.apiType === AdminApiConnectionsListTypeEnum.ISSUER_API)
      ) {
        setIssuerApiConnectionAlreadyExists(true);
      }
    });
  }, []);

  useEffect(() => {
    withApi(async () => {
      if (selectedDistributionPlatform) {
        const res = await adminApi.adminApiConnectionsList({
          distributionPlatform: selectedDistributionPlatform,
          limit: 1,
        });
        if (
          !!res.results.length &&
          res.results.some((connection) => connection.apiType === AdminApiConnectionsListTypeEnum.DISTRIBUTOR_API)
        ) {
          setDistributorConnectionAlreadyExists(true);
        }
      }
    });
  }, [selectedDistributionPlatform]);

  const getDistributionPlatforms = useCallback(
    (searchQuery: string = '') => {
      withApi(async () => {
        try {
          const response = await adminApi.adminDistributionPlatformsList({
            hasFrontend: false,
            ...(currentUserHasManageUsersAndGroupsPermission(currentUser?.permissions || [])
              ? {}
              : { permissions: [OrganizationPermissionsEnum.MANAGE_ORGANIZATION_USERS_AND_GROUPS] }),
            limit: 50,
            search: searchQuery,
          });
          setDistributionPlatforms(
            response.results.map((item) => ({
              value: item.id,
              label: item.displayName,
            })),
          );
        } catch (e) {
          console.error(e);
        }
      });
    },
    [adminApi, user, currentUser],
  );

  const searchDistributionPlatform = useCallback(
    (value: string) => {
      getDistributionPlatforms(value);
    },
    [getDistributionPlatforms],
  );

  useEffect(() => {
    getDistributionPlatforms();
  }, [getDistributionPlatforms]);

  const debouncedDistributionSearch = useMemo(
    () => debounce(searchDistributionPlatform, 500),
    [searchDistributionPlatform],
  );

  const onSubmit = useCallback(
    (fieldsValues: StateValues<AddApiConnectionFields>) => {
      withApi(async () => {
        const params: AdminApiConnectionsCreateRequest = {
          adminApiConnectionCreationRequest: {
            name: fieldsValues.name,
            user: user.id,
            ...(fieldsValues.distributionPlatform &&
              fieldsValues.type === AdminApiConnectionsListTypeEnum.DISTRIBUTOR_API && {
                distributionPlatform: fieldsValues.distributionPlatform,
              }),
          },
        };

        const response = await adminApi.adminApiConnectionsCreate(params);
        setCreatedApiConnection(response);
        onSaveApiConnection();
      });
    },
    [user?.id],
  );

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="addApiConnectionModal"
        initial={{
          type:
            !canAddIssuerApiConnection || hasIssuerApiConnection
              ? AdminApiConnectionsListTypeEnum.DISTRIBUTOR_API
              : AdminApiConnectionsListTypeEnum.ISSUER_API,
        }}
        onSubmit={(values: StateValues<AddApiConnectionFields>) => {
          onSubmit(values);
        }}
        error={error}
      >
        <ModalHeader>
          <Translate name="addApiConnectionModal.title" />
        </ModalHeader>
        <ModalContent>
          <Form.ValueProvider>
            {({ type }) => (
              <>
                <Form.Group
                  name="name"
                  required={true}
                  requiredError={<Translate name="addApiConnectionModal.fields.name.requiredError" />}
                >
                  <Form.Input />
                </Form.Group>
                <Form.Group name="type" required={true}>
                  <Form.GroupToggle>
                    <Form.Radio
                      value={AdminApiConnectionsListTypeEnum.ISSUER_API}
                      disabled={!canAddIssuerApiConnection || hasIssuerApiConnection}
                    >
                      <Translate name="issuerUserApiConnectionsTable.issuerApi" />
                    </Form.Radio>
                    <Form.Radio
                      value={AdminApiConnectionsListTypeEnum.DISTRIBUTOR_API}
                      disabled={!canAddDistributorApiConnection || hasDistributorApiConnection}
                    >
                      <Translate name="issuerUserApiConnectionsTable.distributorApi" />
                    </Form.Radio>
                  </Form.GroupToggle>
                </Form.Group>
                {type === AdminApiConnectionsListTypeEnum.DISTRIBUTOR_API && (
                  <Form.Group
                    name="distributionPlatform"
                    required={true}
                    requiredError={<Translate name="addApiConnectionModal.fields.distributionPlatform.requiredError" />}
                  >
                    <Form.Select
                      options={distributionPlatforms.map(({ value, label }) => ({
                        value: value,
                        label: label,
                      }))}
                      onChange={(val: string) => setSelectedDistributionPlatform(val)}
                      onInputChange={debouncedDistributionSearch}
                    ></Form.Select>
                  </Form.Group>
                )}
                {apiIssuerConnectionAlreadyExists && type === AdminApiConnectionsListTypeEnum.ISSUER_API && (
                  <Hint variant="warning">
                    <div>
                      <strong>
                        <Translate as="p" name="addApiConnectionModal.apiConnectionAlreadyExisting.title" />
                      </strong>
                      <Translate name="addApiConnectionModal.apiConnectionAlreadyExisting.hint" />
                    </div>
                  </Hint>
                )}
                {distributorConnectionAlreadyExists && type === AdminApiConnectionsListTypeEnum.DISTRIBUTOR_API && (
                  <Hint variant="warning">
                    <div>
                      <strong>
                        <Translate as="p" name="addApiConnectionModal.distributorConnectionAlreadyExisting.title" />
                      </strong>
                      <Translate name="addApiConnectionModal.distributorConnectionAlreadyExisting.hint" />
                    </div>
                  </Hint>
                )}
              </>
            )}
          </Form.ValueProvider>
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'send',
                content: <Translate name="common.add" />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddApiConnectionModal;
